import { Badge, Card, Grid, Group, Text, Title } from '@mantine/core';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import prettifiedLabel from '../../constants/labelPrettifier';
import { dateToEndOfDay, dateToStartOfDay } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import AbTestChartView from './AbTestChartView';
import AbTestSummaryElement from './AbTestSummaryElement';
import AbTestTableView from './AbTestTableView';
import AbTestTotalCounts from './AbTestTotalCounts';
import SuperAdminBadge from '../SuperAdmin/SuperAdminBadge';

const statsDebounced = AwesomeDebouncePromise((fetch, fetchUrl) => {
  return fetch(fetchUrl);
}, 300);

/**
 * AbTestVisualization component.
 *
 * Props:
 * - startDate: string - ISO date "YYYY-MM-DD"
 * - endDate: string - ISO date "YYYY-MM-DD"
 */
// Revenue-level charts/results hidden from non-super-admins.
const SUPER_ADMIN_CHARTS = ['averageOrderValueByGroup', 'purchasesByGroup', 'revenuePerProfiles'];

const AbTestVisualization = ({ abTest, startDate, endDate, isSuperAdmin }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const [loadError, setLoadError] = useState();
  const [abTestStats, setAbTestStats] = useState();

  // Convert ISO date strings to full timestamps for the API
  const startTs = startDate ? encodeURIComponent(dateToStartOfDay(startDate, 'UTC')) : null;
  const endTs = endDate ? encodeURIComponent(dateToEndOfDay(endDate, 'UTC')) : null;

  const fetchUrl =
    startTs && endTs
      ? `/api/admin/accounts/${accountId}/domains/${domainId}/abTests/${abTest.id}/stats/${startTs}/${endTs}`
      : null;

  useEffect(() => {
    if (!fetchUrl) return;

    setAbTestStats(undefined);
    statsDebounced(fetch, fetchUrl)
      .then((data) => {
        setAbTestStats(data);
      })
      .catch(setLoadError);
  }, [fetch, fetchUrl]);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (abTestStats === undefined) {
    return <Spinner />;
  }

  const abTestElements = abTestStats.charts
    .filter((chart) => isSuperAdmin || !SUPER_ADMIN_CHARTS.includes(chart.name))
    .map((chart, index) => {
      return (
        <Grid.Col span={{ base: 12, md: 6 }} key={index}>
          <Card withBorder padding="md">
            <Group gap="xs" mb="md">
              <Badge size="lg" radius="sm" styles={{ label: { textTransform: 'uppercase', fontWeight: 600 } }}>
                {prettifiedLabel(chart.name)}
              </Badge>
              {SUPER_ADMIN_CHARTS.includes(chart.name) && <SuperAdminBadge show={isSuperAdmin} />}
            </Group>
            <AbTestChartView chart={chart} />
            <AbTestTableView
              total={abTestStats.tables.total.find((total) => {
                const mapping = {
                  averageOrderValueByGroup: 'purchasesByGroupTotal',
                  pageViewsByGroup: 'profilesByGroupTotal',
                  productViewsByGroup: 'profilesByGroupTotal',
                  purchasesByGroup: 'profilesByGroupTotal',
                  revenueByGroup: 'profilesByGroupTotal',
                  scrollDepthByGroup: 'engagementIntervalsByGroupTotal',
                  timeOnPageByGroup: 'engagementIntervalsByGroupTotal',
                };
                return chart.name in mapping ? total.name === mapping[chart.name] : null;
              })}
              table={abTestStats.tables.statistics.find((data) => data.name === chart.name)}
              results={abTestStats.results.find((data) => data.name === chart.name)}
              variables={abTestStats.zScoresVariables.find((data) => data.name === chart.name)}
            />
          </Card>
        </Grid.Col>
      );
    });

  const summaryElements = abTestStats.results
    .filter((result) => isSuperAdmin || !SUPER_ADMIN_CHARTS.includes(result.name))
    .map((result, index) => {
      return <AbTestSummaryElement key={index} result={result} isSuperAdmin={isSuperAdmin} />;
    });

  const sampleSizeCount = abTestStats.tables.total ? (
    <Card.Section withBorder inheritPadding py="xs">
      <Grid>
        <Grid.Col span={{ base: 12, md: 2 }}>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Group:
            </Text>
          </Group>
          <Title order={5}>Control</Title>
          <Title order={5}>
            Treatment{' '}
            <Text component="span" c="dimmed">
              (with Releva)
            </Text>
          </Title>
        </Grid.Col>
        {abTestStats.tables.total.map((total, index) => (
          <AbTestTotalCounts key={index} total={total} />
        ))}
      </Grid>
    </Card.Section>
  ) : null;

  return (
    <>
      <Card withBorder>
        <Card.Section withBorder inheritPadding py="xs">
          <Grid>
            <Grid.Col span={12}>
              <Group justify="space-between">
                <Text>Results</Text>
              </Group>
            </Grid.Col>
          </Grid>
        </Card.Section>
        <Card.Section inheritPadding py="md">
          <Grid>
            <Grid.Col span="auto">
              <Text size="sm" c="dimmed">
                Indicator:
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 2 }}>
              <Text size="sm" c="dimmed">
                Status:
              </Text>
            </Grid.Col>
            <Grid.Col span="auto">
              <Text size="sm" c="dimmed">
                Influence:
              </Text>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 2 }}>
              <Text size="sm" c="dimmed">
                Statistical significance:
              </Text>
            </Grid.Col>
          </Grid>
          <hr />
          {summaryElements}
        </Card.Section>
        {sampleSizeCount}
      </Card>
      <br />
      <Grid>{abTestElements}</Grid>
    </>
  );
};

export default AbTestVisualization;
